// StylePicker.tsx — §PICKER — the surface. Every decision it renders lives in lib/style-picker.ts.
//
// Accessibility is structural here rather than added afterwards: the pack list is a real radiogroup,
// so arrow keys move between options and the browser announces "3 of 42" without any JS; ineligible
// packs stay in the DOM with aria-disabled and a reason, because "why can't I pick that" is a
// question this surface should answer; and focus is visible from the shared token, never removed.
//
// RTL needs no special case — the layout is logical-property based and inherits `dir` from the app
// shell, which is why there is no `left`/`right` anywhere below.
import { useState, useMemo, useCallback } from 'preact/hooks';
import type { StylePackEntry, StyleIntensity } from '@vibeshell/shared';
import {
  pickerRows, initPicker, preview, cancel, confirm, undo, effectiveStyle,
  availableThemes, availableIntensities, performanceNote, emptyFilters,
  type PickerState, type ThemeChoice, type Viewport,
} from '../lib/style-picker';

export interface StylePickerProps {
  spec: Record<string, unknown> | null;
  /** persists the patch; the picker never writes directly */
  onCommit: (patch: { style: unknown }) => Promise<void> | void;
  /** renders the app under a candidate style, without saving */
  renderPreview: (style: unknown, viewport: Viewport) => preact.ComponentChildren;
  onClose?: () => void;
}

export function StylePicker({ spec, onCommit, renderPreview, onClose }: StylePickerProps) {
  const [state, setState] = useState<PickerState>(() => initPicker(spec));
  const [intensity, setIntensity] = useState<StyleIntensity>('standard');
  const [theme, setTheme] = useState<ThemeChoice>('auto');
  const [busy, setBusy] = useState(false);

  const rows = useMemo(() => pickerRows(state.filters), [state.filters]);
  const shown = effectiveStyle(state);
  const selectedId = shown?.stylePackId ?? null;

  const choose = useCallback((pack: StylePackEntry) => {
    setState((s) => preview(s, pack.id, { intensity, themeMode: theme }));
  }, [intensity, theme]);

  const doConfirm = useCallback(async () => {
    const { state: next, patch } = confirm(state);
    if (!patch) return;
    setBusy(true);
    try { await onCommit(patch); setState(next); }
    catch (e) { setState((s) => ({ ...s, status: 'error', error: e instanceof Error ? e.message : 'could not save the style' })); }
    finally { setBusy(false); }
  }, [state, onCommit]);

  const doUndo = useCallback(async () => {
    const { state: next, patch } = undo(state);
    setBusy(true);
    try { await onCommit(patch as { style: unknown }); setState(next); }
    catch (e) { setState((s) => ({ ...s, status: 'error', error: e instanceof Error ? e.message : 'could not undo' })); }
    finally { setBusy(false); }
  }, [state, onCommit]);

  const setFilter = (patch: Partial<PickerState['filters']>) =>
    setState((s) => ({ ...s, filters: { ...s.filters, ...patch } }));

  return (
    <div class="style-picker" role="dialog" aria-label="Choose a style">
      <div class="style-picker__controls">
        <label class="style-picker__search">
          <span>Search styles</span>
          <input
            type="search" value={state.filters.search} placeholder="name or character"
            onInput={(e) => setFilter({ search: (e.target as HTMLInputElement).value })}
          />
        </label>

        <fieldset>
          <legend>Character</legend>
          {['neutral', 'serif', 'heavy', 'mono', 'soft'].map((v) => (
            <button
              key={v} type="button" aria-pressed={state.filters.voice === v}
              onClick={() => setFilter({ voice: state.filters.voice === v ? null : v })}
            >{v}</button>
          ))}
        </fieldset>

        <fieldset>
          <legend>Density</legend>
          {(['expressive', 'standard', 'utility-dense'] as StyleIntensity[]).map((i) => (
            <button key={i} type="button" aria-pressed={intensity === i} onClick={() => setIntensity(i)}>{i}</button>
          ))}
        </fieldset>

        <fieldset>
          <legend>Preview at</legend>
          {(['desktop', 'mobile'] as Viewport[]).map((v) => (
            <button key={v} type="button" aria-pressed={state.viewport === v}
              onClick={() => setState((s) => ({ ...s, viewport: v }))}>{v}</button>
          ))}
        </fieldset>

        <label>
          <input type="checkbox" checked={state.filters.excludeHeavy}
            onChange={(e) => setFilter({ excludeHeavy: (e.target as HTMLInputElement).checked })} />
          <span>Hide styles that cost frame budget</span>
        </label>
      </div>

      {state.status === 'error' && <p role="alert" class="style-picker__error">{state.error}</p>}
      {state.status === 'unsupported' && <p role="alert" class="style-picker__error">{state.error}</p>}

      <div class="style-picker__list" role="radiogroup" aria-label={`${rows.length} styles`}>
        {rows.map(({ pack, eligible, ineligibleReason, heavy, themes }) => {
          const selected = pack.id === selectedId;
          return (
            <button
              key={pack.id} type="button" role="radio"
              aria-checked={selected} aria-disabled={!eligible}
              // an ineligible pack keeps its place in the tab order so its reason is reachable
              onClick={() => eligible && choose(pack)}
              class={`style-picker__pack${selected ? ' is-selected' : ''}${eligible ? '' : ' is-ineligible'}`}
            >
              <span class="style-picker__name">{pack.name}</span>
              <span class="style-picker__voice">{pack.scriptVoice}</span>
              <span class="style-picker__themes">{themes.join(' · ')}</span>
              {heavy && <span class="style-picker__heavy" title={performanceNote(pack, 'expressive') ?? ''}>heavy</span>}
              {!eligible && <span class="style-picker__why">{ineligibleReason}</span>}
            </button>
          );
        })}
      </div>

      {selectedId && (
        <fieldset class="style-picker__theme">
          <legend>Appearance</legend>
          {/* only the themes THIS pack declares — 39 of 42 ship light, 41 ship dark */}
          {availableThemes(rows.find((r) => r.pack.id === selectedId)!.pack).map((t) => (
            <button key={t} type="button" aria-pressed={theme === t}
              onClick={() => { setTheme(t); setState((s) => preview(s, selectedId, { intensity, themeMode: t })); }}>{t}</button>
          ))}
        </fieldset>
      )}

      <div class="style-picker__preview" data-viewport={state.viewport} aria-live="polite">
        {renderPreview(shown, state.viewport)}
      </div>

      <div class="style-picker__actions">
        <button type="button" onClick={doConfirm} disabled={!state.candidate || busy}>
          {busy ? 'Saving…' : 'Use this style'}
        </button>
        <button type="button" onClick={() => setState(cancel)} disabled={!state.candidate || busy}>Cancel</button>
        <button type="button" onClick={doUndo} disabled={!state.undoTo || busy}>Undo last change</button>
        {onClose && <button type="button" onClick={onClose}>Close</button>}
      </div>
    </div>
  );
}
